import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApplyLink from './ApplyLink';
import './AnnouncementBar.css';

const STORAGE_KEY = 'aen_announcement_dismissed';

// Hard-stop date. The "September 2026 intake" urgency copy stops making
// sense after enrolment starts, so the bar auto-hides site-wide from
// 1 October 2026 onward. When you rotate the announcement copy for the
// next intake cycle, update both the translation key `announcement_text`
// AND this date. Using ISO 8601 with explicit Z so timezones can't shift
// the cutoff by a day.
const EXPIRES_AT = new Date('2026-10-01T00:00:00Z');

// Synchronous initial-visibility check. Used to live in a useEffect that
// flipped `visible` from false → true after mount, but that caused a
// hydration mismatch with the static-skeleton in public/index.html
// (which renders the bar visible by default). Computing the value
// synchronously in useState's initialiser means React's first render
// output matches the static skeleton, so hydrateRoot in src/index.js
// can reuse the existing DOM nodes instead of unmounting + replacing
// (which destroyed the LCP-relevant subtrees on cold loads — see the
// Option IIb LCP-variance fix from 2026-05-04).
function initialVisible() {
  if (Date.now() >= EXPIRES_AT.getTime()) return false;
  if (typeof window === 'undefined') return true; // SSR/static — show by default
  try {
    return !window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return true; // sessionStorage disabled (Safari Private quota etc.) — show
  }
}

export default function AnnouncementBar() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(initialVisible);

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="announcement-bar">
      <div className="announcement-inner">
        <span className="announcement-text">
          {t('announcement_text')}{' '}
          <ApplyLink className="announcement-link">{t('apply_now')}</ApplyLink>
        </span>
        <button className="announcement-close" onClick={dismiss} aria-label={t('announcement_dismiss')}>
          ✕
        </button>
      </div>
    </div>
  );
}
